import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <Seo title="Refund & Cancellation Policy" description="Premium membership refund and cancellation terms." path="/refund-policy" />
      <div className="container-luxury max-w-4xl">
        <div className="bg-white p-8 md:p-12 rounded-sm shadow-sm border border-border/50">
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#0F172A] mb-8">Refund & Cancellation Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2026</p>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
            <h2 className="text-red-700 font-bold text-xl mb-2">Important Notice</h2>
            <p className="text-red-700">
              All premium membership purchases on Bharat Estate are FINAL and NON-REFUNDABLE. Please read this policy carefully before making any purchase.
            </p>
          </div>

          <div className="prose prose-lg max-w-none text-[#0F172A]">
            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">1. Premium Membership - No Refund Policy</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Bharat Estate operates on a strict NO REFUND policy for all premium membership subscriptions. This policy applies to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li><strong>Monthly Premium Membership</strong> (₹999)</li>
                <li><strong>Quarterly Premium Membership</strong> (₹2,499)</li>
                <li><strong>Yearly Premium Membership</strong> (₹7,999)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">2. Why No Refunds?</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Premium memberships are non-refundable because:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li><strong>Instant Activation:</strong> Membership benefits are activated immediately upon payment</li>
                <li><strong>Digital Service:</strong> Premium features are digital services that cannot be "returned"</li>
                <li><strong>Platform Access:</strong> Once activated, you gain immediate access to all premium features</li>
                <li><strong>Infrastructure Costs:</strong> Resources are allocated immediately for premium users</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">3. Situations Where Refunds Are NOT Provided</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Refunds will NOT be provided in the following situations:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Change of mind after purchase</li>
                <li>Unused portion of membership period</li>
                <li>Dissatisfaction with services</li>
                <li>Account suspension or termination due to Terms violation</li>
                <li>Inability to list properties due to incomplete profile</li>
                <li>Technical issues on user's end (internet, device, etc.)</li>
                <li>Duplicate purchases made by mistake</li>
                <li>Failure to use the services within the membership period</li>
                <li>Business closure or change in circumstances</li>
                <li>Moving to a different platform</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">4. Cancellation Policy</h2>
              
              <h3 className="font-semibold text-lg mb-3">4.1 Membership Cancellation</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You may cancel your premium membership at any time, but:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>No refund will be provided for the remaining period</li>
                <li>Your premium features will remain active until the expiry date</li>
                <li>Your existing listings will remain active until membership expires</li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">4.2 Account Deletion</h3>
              <p className="text-muted-foreground leading-relaxed">
                If you delete your account while having an active premium membership, you forfeit all remaining benefits. No refund will be provided.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">5. Delivery/Activation Policy</h2>
              
              <h3 className="font-semibold text-lg mb-3">5.1 Instant Activation</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Premium membership is activated INSTANTLY upon successful payment. There is no waiting period or manual approval process.
              </p>

              <h3 className="font-semibold text-lg mb-3">5.2 Activation Confirmation</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Upon successful activation, you will receive:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>On-screen confirmation of membership activation</li>
                <li>Email confirmation with membership details</li>
                <li>Updated account status showing premium benefits</li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">5.3 Payment Failure</h3>
              <p className="text-muted-foreground leading-relaxed">
                If payment fails but your account is charged, please contact our support team immediately. We will verify the transaction and either activate your membership or facilitate a refund within 7-10 business days.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">6. Exceptions (Rare Cases)</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Refunds may be considered ONLY in these exceptional circumstances:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li><strong>Double Charge:</strong> If you were charged twice for the same subscription due to a system error</li>
                <li><strong>Payment Error:</strong> If payment was deducted but membership was not activated (not due to user error)</li>
                <li><strong>Unauthorized Transaction:</strong> If the transaction was made without your authorization (subject to investigation)</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                All exception requests are subject to verification and approval at our sole discretion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">7. How to Request Refund (For Exceptions Only)</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you believe you qualify for an exception, submit a request within 48 hours of payment:
              </p>
              <ol className="list-decimal list-inside text-muted-foreground mb-4 space-y-2">
                <li>Email contact@bharat.estate with subject "Refund Request - [Your Email]"</li>
                <li>Include your registered email address</li>
                <li>Provide transaction/payment ID</li>
                <li>Explain the reason for refund request</li>
                <li>Attach any supporting documents (screenshots, bank statements, etc.)</li>
              </ol>
              <p className="text-muted-foreground leading-relaxed">
                We will review your request and respond within 3-5 business days.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">8. Refund Processing Time</h2>
              <p className="text-muted-foreground leading-relaxed">
                If a refund is approved under exceptional circumstances, it will be processed within 7-10 business days to the original payment method. Bank processing times may add 3-5 additional days.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">9. Acknowledgment</h2>
              <div className="bg-gray-100 p-6 rounded-sm">
                <p className="text-[#0F172A] leading-relaxed">
                  By purchasing a premium membership, you acknowledge that you have read, understood, and agree to this Refund & Cancellation Policy. You understand that premium memberships are non-refundable and that membership benefits are activated immediately upon payment.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">10. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For questions about this policy or refund requests:
              </p>
              <div className="bg-[#F9F9F7] p-6 rounded-sm">
                <p className="text-[#0F172A] font-medium">Bharat Estate</p>
                <p className="text-muted-foreground">Email: contact@bharat.estate</p>
                <p className="text-muted-foreground">Phone: +91 94650 24516</p>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t flex gap-4">
            <Link to="/privacy" className="text-orange-500 hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-orange-500 hover:underline">Terms of Service</Link>
            <Link to="/grievance" className="text-orange-500 hover:underline">Grievance Redressal</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicyPage;
